import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages — KEEP EAGER: Login/Register/Dashboard (first paint on auth check)
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";

// iter59 LCP fix: lazy-load all secondary pages. Each chunk is fetched ON DEMAND
// when its route is visited, slashing the initial JS bundle from ~800KB → ~200KB.
// Shopify's "Admin performance" benchmark measures LCP — smaller bundle = faster paint.
const Products = lazy(() => import("./pages/Products"));
const ProductForm = lazy(() => import("./pages/ProductForm"));
const ProductPreview = lazy(() => import("./pages/ProductPreview"));
const OptionSets = lazy(() => import("./pages/OptionSets"));
const OptionSetForm = lazy(() => import("./pages/OptionSetForm"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ConditionalLogic = lazy(() => import("./pages/ConditionalLogic"));
const Settings = lazy(() => import("./pages/Settings"));
const VariantMapping = lazy(() => import("./pages/VariantMapping"));
const StorefrontWidget = lazy(() => import("./pages/StorefrontWidget"));
const WidgetCustomizer = lazy(() => import("./pages/WidgetCustomizer"));
const Billing = lazy(() => import("./pages/Billing"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const BridgeProducts = lazy(() => import("./pages/BridgeProducts"));
const Bundles = lazy(() => import("./pages/Bundles"));
const BundleForm = lazy(() => import("./pages/BundleForm"));
const Translations = lazy(() => import("./pages/Translations"));

// Shared fallback for Suspense boundaries — minimal spinner, no layout shift.
const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center" data-testid="page-loading">
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Inside Shopify embedded iframe: do NOT redirect to /login (that breaks Shopify's
    // embedded experience policy 2.2.2). Show a friendly retry message; the AuthContext
    // session token refresher will pick the session up once App Bridge is ready.
    const inIframe = (() => { try { return window.self !== window.top; } catch (e) { return true; } })();
    const isEmbeddedCtx = inIframe || !!window.shopify || !!window.__SHOPIFY_EMBEDDED__;
    if (isEmbeddedCtx) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground max-w-sm">
            Loading your store session… If this persists, please open the app from Shopify Admin → Apps.
          </p>
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            <ProductForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <ProtectedRoute>
            <ProductForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/preview"
        element={
          <ProtectedRoute>
            <ProductPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/option-sets"
        element={
          <ProtectedRoute>
            <OptionSets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/option-sets/new"
        element={
          <ProtectedRoute>
            <OptionSetForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/option-sets/:id"
        element={
          <ProtectedRoute>
            <OptionSetForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing-rules"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/logic"
        element={
          <ProtectedRoute>
            <ConditionalLogic />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/variant-mapping"
        element={
          <ProtectedRoute>
            <VariantMapping />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bridge-products"
        element={
          <ProtectedRoute>
            <BridgeProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bundles"
        element={
          <ProtectedRoute>
            <Bundles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bundles/:id"
        element={
          <ProtectedRoute>
            <BundleForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/translations"
        element={
          <ProtectedRoute>
            <Translations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/widget"
        element={
          <ProtectedRoute>
            <StorefrontWidget />
          </ProtectedRoute>
        }
      />
      <Route
        path="/widget-customizer"
        element={
          <ProtectedRoute>
            <WidgetCustomizer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Public Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Error Boundary for catching render errors
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App crash:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', padding: 20 }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <h2 style={{ fontSize: 20, marginBottom: 12 }}>Something went wrong</h2>
            <p style={{ fontSize: 13, color: '#999', marginBottom: 16 }}>{String(this.state.error)}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    window.__APP_LOADED__ = true;
  }, []);

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
